import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, isNil, map } from 'ramda'
import { Checkbox, Link } from '../../../ui'
import { useStore } from '../../../StateProvider'
import { getUrlLang } from '../../../i18n/i18nUtils'
import { usePresetValue } from '../../utils/hooks'
import { getUserValue, useGetPlatform } from '../utils'
import queryString from 'query-string'
import { CONTEXT } from '../../../constants/routes'
import { useLocation } from 'react-router-dom'
import i18n from 'i18next'
import { isPammRef } from '../../utils/utils'

const camelToSnakeCase = str => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)

const ExternalCheckbox = ({ name, label, text, linkText, url, required, showLabel, urlParams, showForBrokerGroup }) => {
  const { t } = useTranslation()
  const [isChecked, setIsChecked] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [{ user, form: { accId } }, dispatch] = useStore()
  const platform = useGetPlatform()
  const txt = text ? t(`generatedField.${text}`) : ''
  const location = useLocation()

  const curUrl = useMemo(() => {
    const { protocol, host, search } = window.location
    let curUrl = typeof url === 'object' ? getUrlLang(url) : url
    curUrl = curUrl?.indexOf('https') === -1
      ? `${protocol}//${host}/${CONTEXT}/${curUrl}`
      : curUrl
    if (isNil(urlParams) || isEmpty(urlParams)) return curUrl

    const query = queryString.parse(search)

    const params = urlParams.reduce((acc, curParam) => {
      if (curParam === 'platform') {
        return platform?.length ? { ...acc, platform } : acc
      } else {
        const paramValue = query[camelToSnakeCase(curParam)] || query[curParam]
        return paramValue?.length ? { ...acc, [curParam]: paramValue } : acc
      }
    }, [])
    let strParams = queryString.stringify(params)
    strParams = strParams.length ? `?${strParams}` : strParams
    return `${curUrl}${strParams}`
  }, [url, urlParams, t, i18n.language])

  useEffect(() => {
    const { fund_id: fundId, broker_group: brokerGroup, ref_id: refId } = queryString.parse(location.search)
    const existBrokerGroup = brokerGroup || user?.brokerGroup

    if (
      ((name === 'lpoaDocAgree' || name === 'compensationAckDocAgree') &&
        !fundId && !isPammRef(refId)) ||
      (showForBrokerGroup && existBrokerGroup !== showForBrokerGroup)
    ) {
      setIsVisible(false)
      dispatch({ type: 'removeFieldValue', name })
      dispatch({ type: 'removeValidationFld', name })
    }
  }, [name, urlParams])

  const linkTxt = useMemo(() => {
    if (linkText) {
      if (linkText.startsWith('{')) {
        try {
          const parsed = JSON.parse(linkText)
          return parsed[i18n.language] || parsed.en
        } catch (e) {
          console.log("Can't parse linkTxt object")
        }
      }
      return t(`generatedField.${linkText}`)
    }
    return ''
  }, [linkText, t, i18n.language])
  const userValue = getUserValue(accId, user, name)

  const presetValue = (value) => {
    setIsChecked(value)
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value })
  }

  usePresetValue(name, userValue, presetValue)

  const onChange = () => {
    const value = !isChecked
    setIsChecked(isChecked => !isChecked)

    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value })
  }

  return isVisible ? (
    <Checkbox
      key={`checkbox-${name}`}
      name={name}
      onChange={onChange}
      label={showLabel ? t(label) : null}
      isChecked={isChecked}
    >
      {txt}
      <Link href={curUrl} target='_blank'>
        {linkTxt}
      </Link>
    </Checkbox>
  ) : null
}

const getExternalCheckboxFields = showLabelAsPlaceholder =>
  map(checkboxField => {
    const { i18n, name, required, text, linkText, url, order, showLabel, urlParams, showForBrokerGroup } = checkboxField
    return (
      <ExternalCheckbox
        label={i18n ? `generatedField.${i18n}` : ''}
        name={name}
        text={text}
        linkText={linkText}
        url={url}
        hideLabel={showLabelAsPlaceholder}
        showLabel={showLabel}
        required={required}
        key={name}
        order={order}
        urlParams={urlParams}
        showForBrokerGroup={showForBrokerGroup}
      />
    )
  })

export { ExternalCheckbox, getExternalCheckboxFields }
